import React, { useState, useRef, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import LoopRoundedIcon from "@mui/icons-material/LoopRounded";

import { brand, fonts } from "../../themes/theme";

const W = 920;
const H = 500;

// Solo 3 tipos en el lienzo: activo, terminal (fin de recorrido) y
// paralelo/dormido. El abandono NO es un tipo de nodo: es informacion interna
// (tasa + razones + fuente) que aparece en el popover al pasar el raton por
// el nodo del que se abandona. Nada es clicable: solo hover.
const TYPE_COLOR = {
    flow: brand.primary,
    terminal: brand.navy,
    parallel: brand.teal,
};

// Layout tipo "rio": serpentea sobre todo en VERTICAL para avanzar sin
// estirarse tanto en horizontal (nada de tramo largo y recto). Ramas
// (embriones) como tallo corto justo junto a su nodo de origen.
// `highFuga`: los 2 puntos con mayor caida conocida -> borde degradado a naranja.
const NODES = {
    consulta: { x: 60, y: 265, type: "flow" },
    diagnostico: { x: 185, y: 195, type: "flow", highFuga: true },
    tto1aLinea: { x: 305, y: 320, type: "flow" },
    cicloTra: { x: 450, y: 230, type: "flow", big: true, highFuga: true },
    embriones: { x: 450, y: 90, type: "parallel" },
    resultadoBeta: { x: 600, y: 295, type: "flow" },
    betaPositiva: { x: 705, y: 175, type: "flow", small: true },
    betaNegativa: { x: 715, y: 410, type: "flow", small: true },
    embarazoEvolutivo: { x: 790, y: 110, type: "flow", small: true },
    partoNacimiento: { x: 850, y: 205, type: "flow", small: true },
    cierreEpisodio: { x: 835, y: 335, type: "terminal", small: true },
    finTratamiento: { x: 790, y: 430, type: "terminal", small: true },
};

// curve = desplazamiento del punto de control (siempre curvo, nunca recto,
// pero MODERADO: nada de arcos que cruzan todo el lienzo). Sin `dashed`, la
// arista es un tramo de flujo real; `dashed` = rama paralela, bucle o de baja
// evidencia. NINGUNA arista apunta a un nodo de abandono: no existen.
// `dots` decrece a lo largo del tronco: muchas bolitas al inicio, cada vez
// menos segun avanza (visualiza la fuga acumulada sin necesidad de texto).
const EDGES = [
    { from: "consulta", to: "diagnostico", dots: 22, curve: 25 },
    { from: "diagnostico", to: "tto1aLinea", dots: 15, curve: -30 },
    { from: "diagnostico", to: "cicloTra", dots: 11, curve: -70 },
    { from: "tto1aLinea", to: "cicloTra", dots: 9, curve: -35 },
    { from: "tto1aLinea", to: "resultadoBeta", dots: 6, curve: 55 },
    { from: "cicloTra", to: "resultadoBeta", dots: 9, curve: 30 },
    { from: "resultadoBeta", to: "betaPositiva", dots: 5, curve: -30 },
    { from: "resultadoBeta", to: "betaNegativa", dots: 5, curve: 35 },
    { from: "betaPositiva", to: "embarazoEvolutivo", dots: 3, curve: -25 },
    { from: "embarazoEvolutivo", to: "partoNacimiento", dots: 3, curve: -20 },
    { from: "partoNacimiento", to: "cierreEpisodio", dots: 2, curve: 20 },
    { from: "betaNegativa", to: "finTratamiento", dots: 3, curve: 25 },
    { from: "betaNegativa", to: "cicloTra", dashed: true, curve: -120, dots: 3 },
    { from: "cicloTra", to: "embriones", dashed: true, curve: 20, dots: 2 },
    // La rama de embriones puede seguir abierta aunque el episodio se cierre
    // (concurrencia): arco discontinuo por arriba de vuelta a criopreservacion.
    { from: "cierreEpisodio", to: "embriones", dashed: true, curve: -25, dots: 2 },
];

const rnd = (a) => {
    const x = Math.sin(a) * 43758.5453;
    return x - Math.floor(x);
};

// Borde solido normal, o degradado hacia naranja para los nodos de mayor fuga.
function nodeBorderSx(color, width, highFuga) {
    if (!highFuga) {
        return { border: `${width}px solid ${color}`, bgcolor: "#fff" };
    }
    return {
        border: `${width}px solid transparent`,
        backgroundImage: `linear-gradient(#fff, #fff), linear-gradient(135deg, ${color}, ${brand.accent})`,
        backgroundOrigin: "border-box",
        backgroundClip: "padding-box, border-box",
    };
}

/**
 * Grafo del recorrido, simple a proposito: tronco casi recto + ramas cortas
 * justo debajo de su origen. Todo es hover-only (nada es clicable): pasar el
 * raton muestra el detalle y hace latir el nodo; al quitar el raton, el
 * detalle desaparece. Los 2 nodos de mayor fuga conocida (Diagnostico, Ciclo
 * TRA) llevan un borde degradado hacia naranja.
 */
function JourneyGraph() {
    const { t } = useTranslation();
    const [hoveredId, setHoveredId] = useState(null);
    const n = (k) => t(`views.journey.graph.nodes.${k}`);

    // El escenario tiene tamano fijo (W x H); lo escalamos para llenar el panel
    // disponible (crece en pantallas grandes, sin dejar mucho borde alrededor;
    // encoge en pequenas). Escala uniforme -> nodos, trazado y texto crecen
    // juntos sin desalinearse.
    const wrapRef = useRef(null);
    const [scale, setScale] = useState(1);
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        const compute = () => {
            const cw = el.clientWidth;
            const ch = el.clientHeight;
            if (!cw || !ch) return;
            // Solo AGRANDAR para llenar el panel (nunca por debajo de 1): en
            // pantallas grandes crece y quita el borde sobrante; en pequenas
            // se queda a tamano natural y el panel hace scroll.
            const s = Math.min(cw / W, ch / H) * 0.86;
            setScale(Math.max(1, Math.min(s, 1.35)));
        };
        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1, minHeight: 0 }}>
            <Legend t={t} />
            <Box ref={wrapRef} sx={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", justifyContent: "safe center", alignItems: "safe center" }}>
                <Box sx={{ position: "relative", width: W, height: H, flexShrink: 0, transform: `scale(${scale})`, transformOrigin: "center center" }}>
                    <Box component="svg" viewBox={`0 0 ${W} ${H}`} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}>
                        {EDGES.map((edge, i) => (
                            <Edge key={i} edge={edge} index={i} />
                        ))}
                    </Box>

                    {Object.entries(NODES).map(([id, pos]) => {
                        if (id === "cicloTra") {
                            return (
                                <CicloTraNode
                                    key={id}
                                    t={t}
                                    pos={pos}
                                    label={n("cicloTra")}
                                    hovered={hoveredId === id}
                                    onEnter={() => setHoveredId(id)}
                                    onLeave={() => setHoveredId(null)}
                                />
                            );
                        }
                        return (
                            <Node
                                key={id}
                                pos={pos}
                                label={n(id)}
                                hovered={hoveredId === id}
                                onEnter={() => setHoveredId(id)}
                                onLeave={() => setHoveredId(null)}
                            />
                        );
                    })}

                    {hoveredId && (
                        <NodeInfoPopover
                            pos={NODES[hoveredId]}
                            title={n(hoveredId)}
                            info={t(`views.journey.graph.nodeInfo.${hoveredId}`, { returnObjects: true })}
                            accent={NODES[hoveredId].highFuga ? brand.accent : TYPE_COLOR[NODES[hoveredId].type]}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
}

// Panel de detalle que aparece al lado del nodo con el raton encima (solo
// hover, nunca clic). Barra de color a la izquierda segun el tipo del nodo
// (naranja si es un punto de alta fuga) + entrada suave. Incluye la
// fuga/info interna, los bucles y los caveats -- nada de eso va suelto en el
// lienzo. Se coloca a la derecha por defecto; si el nodo esta cerca del
// borde derecho, se coloca a la izquierda.
function NodeInfoPopover({ pos, title, info, accent }) {
    const width = 250;
    const gap = 46;
    const showLeft = pos.x > W - 360;
    const desc = typeof info === "string" ? info : info?.desc;
    const facts = Array.isArray(info?.facts) ? info.facts : [];
    return (
        <Box
            sx={{
                position: "absolute",
                left: showLeft ? pos.x - gap - width : pos.x + gap,
                top: Math.min(Math.max(pos.y - 40, 10), H - 170),
                width,
                display: "flex",
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "#fff",
                boxShadow: "0 14px 32px rgba(10,36,62,0.22), 0 2px 8px rgba(10,36,62,0.1)",
                zIndex: 6,
                pointerEvents: "none",
                animation: "mentialFadeUp 0.18s ease",
            }}
        >
            <Box sx={{ width: 4, flexShrink: 0, bgcolor: accent }} />
            <Box sx={{ p: 1.5 }}>
                <Typography sx={{ fontFamily: fonts.display, fontWeight: 400, fontSize: 14.5, color: "text.primary", mb: 0.5 }}>
                    {title}
                </Typography>
                {desc && (
                    <Typography sx={{ fontSize: 11.5, color: "text.secondary", lineHeight: 1.5 }}>{desc}</Typography>
                )}
                {facts.length > 0 && (
                    <Box component="ul" sx={{ listStyle: "none", m: 0, mt: 1, p: 0, display: "flex", flexDirection: "column", gap: 0.6 }}>
                        {facts.map((f, i) => (
                            <Box
                                key={i}
                                component="li"
                                sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}
                            >
                                <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: accent, flexShrink: 0, mt: "6px" }} />
                                <Typography sx={{ fontSize: 11.5, color: "text.primary", lineHeight: 1.4 }}>{f}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function Edge({ edge, index }) {
    const a = NODES[edge.from];
    const b = NODES[edge.to];
    if (!a || !b) return null;

    const midX = (a.x + b.x) / 2;
    const midY = (a.y + b.y) / 2;
    const offset = edge.curve ?? 35;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const px = -dy / len;
    const py = dx / len;
    const ctrlX = midX + px * offset;
    const ctrlY = midY + py * offset;

    const pathId = `edge-${index}`;
    // `cubic` [c1x,c1y,c2x,c2y]: control total para rutas que deben rodear por
    // el perimetro (no cruzar el centro); si no, curva cuadratica via `curve`.
    const d = edge.cubic
        ? `M${a.x},${a.y} C${edge.cubic[0]},${edge.cubic[1]} ${edge.cubic[2]},${edge.cubic[3]} ${b.x},${b.y}`
        : `M${a.x},${a.y} Q${ctrlX},${ctrlY} ${b.x},${b.y}`;
    const color = edge.weak ? brand.gray500 : edge.dashed ? brand.teal : brand.primary;
    const dots = edge.dots || 0;
    const strokeWidth = edge.dashed ? 6 : 14;

    return (
        <g opacity={edge.weak ? 0.65 : 1}>
            <path
                d={d}
                id={pathId}
                fill="none"
                stroke={color}
                strokeWidth={strokeWidth}
                strokeDasharray={edge.dashed ? "4 9" : undefined}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                opacity={edge.dashed ? 0.6 : 0.85}
            />
            {Array.from({ length: dots }).map((_, k) => {
                const seed = index * 97 + k * 7 + 3;
                const dur = (1.5 + rnd(seed * 2.7) * 1.7).toFixed(2);
                const begin = (-(k / Math.max(dots, 1)) * dur).toFixed(2);
                return (
                    <circle key={k} r={2.6} fill="#fff" stroke={color} strokeWidth={1.1} opacity={0.9}>
                        <animateMotion dur={`${dur}s`} repeatCount="indefinite" begin={`${begin}s`}>
                            <mpath href={`#${pathId}`} />
                        </animateMotion>
                    </circle>
                );
            })}
        </g>
    );
}

function Node({ pos, label, hovered, onEnter, onLeave }) {
    const color = TYPE_COLOR[pos.type];
    const size = pos.tiny ? 34 : pos.small ? 44 : 56;
    const width = pos.type === "terminal" ? 3 : 2.5;
    return (
        <React.Fragment>
            {hovered && (
                <Box
                    sx={{
                        position: "absolute",
                        left: pos.x,
                        top: pos.y,
                        width: size,
                        height: size,
                        transform: "translate(-50%, -50%)",
                        pointerEvents: "none",
                        zIndex: 1,
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "50%",
                            border: `2px solid ${pos.highFuga ? brand.accent : color}`,
                            animation: "mentialPulse 1.3s ease-in-out infinite",
                        }}
                    />
                </Box>
            )}
            <Box
                onMouseEnter={onEnter}
                onMouseLeave={onLeave}
                sx={{
                    position: "absolute",
                    left: pos.x,
                    top: pos.y,
                    width: size,
                    height: size,
                    transform: hovered ? "translate(-50%, -50%) scale(1.3)" : "translate(-50%, -50%) scale(1)",
                    transition: "transform .18s ease",
                    borderRadius: "50%",
                    ...nodeBorderSx(color, width, pos.highFuga),
                    boxShadow: hovered ? "0 6px 16px rgba(10,36,62,0.22)" : "0 3px 10px rgba(10,36,62,0.14)",
                    zIndex: hovered ? 5 : 2,
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    left: pos.x,
                    top: pos.y + size / 2 + 6,
                    transform: "translate(-50%, 0)",
                    width: pos.tiny ? 100 : 130,
                    textAlign: "center",
                    zIndex: 3,
                    pointerEvents: "none",
                }}
            >
                <Typography
                    sx={{
                        fontSize: pos.tiny ? 10.5 : 11.5,
                        fontWeight: 500,
                        color: "text.primary",
                        lineHeight: 1.25,
                        textShadow: "0 0 5px #fff, 0 0 5px #fff, 0 0 8px #fff",
                    }}
                >
                    {label}
                </Typography>
            </Box>
        </React.Fragment>
    );
}

// El bucle del Ciclo TRA: hover-only, igual que el resto. Al pasar el raton
// solo se ve su popover de detalle (como el resto de nodos); el sub-proceso
// interno vive en el drill-down "Sub-proceso interno" del embudo, no aqui.
function CicloTraNode({ t, pos, label, hovered, onEnter, onLeave }) {
    const size = 68;

    return (
        <React.Fragment>
            {hovered && (
                <Box
                    sx={{
                        position: "absolute",
                        left: pos.x,
                        top: pos.y,
                        width: size,
                        height: size,
                        transform: "translate(-50%, -50%)",
                        pointerEvents: "none",
                        zIndex: 1,
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "50%",
                            border: `2px solid ${brand.accent}`,
                            animation: "mentialPulse 1.3s ease-in-out infinite",
                        }}
                    />
                </Box>
            )}
            <Box
                onMouseEnter={onEnter}
                onMouseLeave={onLeave}
                sx={{
                    position: "absolute",
                    left: pos.x,
                    top: pos.y,
                    width: size,
                    height: size,
                    transform: hovered ? "translate(-50%, -50%) scale(1.15)" : "translate(-50%, -50%) scale(1)",
                    transition: "transform .18s ease",
                    borderRadius: "50%",
                    ...nodeBorderSx(brand.primary, 3, true),
                    boxShadow: hovered ? "0 8px 18px rgba(10,36,62,0.24)" : "0 4px 12px rgba(10,36,62,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: hovered ? 5 : 2,
                }}
            >
                <LoopRoundedIcon sx={{ fontSize: 26, color: "primary.main" }} />
            </Box>
            <Box
                sx={{
                    position: "absolute",
                    left: pos.x,
                    top: pos.y + size / 2 + 6,
                    transform: "translate(-50%, 0)",
                    width: 160,
                    textAlign: "center",
                    zIndex: 3,
                    pointerEvents: "none",
                }}
            >
                <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: "text.primary", lineHeight: 1.2, textShadow: "0 0 5px #fff, 0 0 5px #fff, 0 0 8px #fff" }}>
                    {label}
                </Typography>
            </Box>
        </React.Fragment>
    );
}

function Legend({ t }) {
    const items = [
        { type: "flow", key: "flow" },
        { type: "terminal", key: "terminal" },
        { type: "parallel", key: "parallel" },
    ];
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", flexShrink: 0 }}>
            {items.map((it) => (
                <Box key={it.key} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box
                        sx={{
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            border: `2px ${it.type === "parallel" ? "dashed" : "solid"} ${TYPE_COLOR[it.type]}`,
                            bgcolor: "#fff",
                        }}
                    />
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{t(`views.journey.graph.legend.${it.key}`)}</Typography>
                </Box>
            ))}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: "50%", border: `2px solid ${brand.accent}`, bgcolor: "#fff" }} />
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{t("views.journey.graph.legend.highFuga")}</Typography>
            </Box>
            <Typography sx={{ fontSize: 10.5, color: "text.secondary", ml: "auto" }}>
                {t("views.journey.graph.hoverHint")}
            </Typography>
        </Box>
    );
}

export default JourneyGraph;
